"use client";

import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";

interface NorrisLinkProps {
  href: string;
  children: string;
  className?: string;
}

const GLITCH_CHARS = "!<>-_\\/[]{}-=+*^?#________";

export default function NorrisLink({ href, children, className = "" }: NorrisLinkProps) {
  const [isMounted, setIsMounted] = useState(false);
  const chars = children.split("");

  // Fix: Ensure we only render random content on the client
  useEffect(() => {
    setIsMounted(true);
  }, []);

  const getRandomChar = () => GLITCH_CHARS[Math.floor(Math.random() * GLITCH_CHARS.length)];

  return (
    <motion.a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className={`relative inline-flex flex-col overflow-hidden group py-1 ${className}`}
      initial="initial"
      whileHover="hover"
      whileTap="tap"
    >
      {/* LAYER 1: STABLE STATE */}
      <div className="flex" aria-hidden="true">
        {chars.map((char, i) => (
          <motion.span
            key={i}
            variants={{
              initial: { y: 0, opacity: 1 },
              hover: { y: "-120%", opacity: 0 },
              tap: { y: "-120%", opacity: 0 }
            }}
            transition={{ delay: i * 0.01, ease: "easeInOut", duration: 0.15 }}
            className="text-white/75 group-hover:text-blue-400 transition-colors duration-200"
          >
            {char === " " ? "\u00A0" : char}
          </motion.span>
        ))}
      </div>

      {/* LAYER 2: GLITCH STATE */}
      <div className="flex absolute inset-0 pointer-events-none mt-1">
        {chars.map((char, i) => (
          <motion.span
            key={i}
            variants={{
              initial: { y: "120%", opacity: 0 },
              hover: { y: 0, opacity: 1 },
              tap: { y: 0, opacity: 1 }
            }}
            transition={{ delay: i * 0.01, ease: "easeInOut", duration: 0.15 }}
            className="text-blue-500 font-black relative"
          >
            <span className="relative z-10">{char === " " ? "\u00A0" : char}</span>
            
            {/* Render the glitch overlay ONLY after mounting to avoid hydration mismatch */}
            {isMounted && (
              <motion.span
                className="absolute inset-0 z-20 text-blue-300 font-mono opacity-0"
                variants={{
                  hover: {
                    opacity: [0, 1, 1, 0],
                    transition: { 
                      duration: 0.3, 
                      delay: i * 0.01,
                      times: [0, 0.1, 0.8, 1] 
                    }
                  }
                }}
              >
                {getRandomChar()}
              </motion.span>
            )}
          </motion.span>
        ))}
      </div>
      
      <motion.div 
        className="absolute bottom-0 left-0 w-full h-[1px] bg-blue-600/50 origin-left"
        variants={{
          initial: { scaleX: 0, opacity: 0 },
          hover: { scaleX: 1, opacity: 1 },
          tap: { scaleX: 1, opacity: 1 }
        }}
        transition={{ duration: 0.3 }}
      />
    </motion.a>
  );
}